// src/math/index.ts
function round(value, decimals = 2) {
  const factor = Math.pow(10, decimals);
  return Math.round(value * factor) / factor;
}
function roundCents(value) {
  return round(value, 2);
}
function toPercent(decimal) {
  return round(decimal * 100, 4);
}
function fromPercent(percent) {
  return percent / 100;
}
function compoundGrowth(principal, rate, periods) {
  return principal * Math.pow(1 + rate, periods);
}
function presentValue(futureValue2, rate, periods) {
  return futureValue2 / Math.pow(1 + rate, periods);
}
function futureValue(presentValue2, rate, periods) {
  return presentValue2 * Math.pow(1 + rate, periods);
}
function calculateMonthlyPayment(principal, annualRate, termMonths) {
  if (principal <= 0 || termMonths <= 0) return 0;
  if (annualRate === 0) return principal / termMonths;
  const monthlyRate = annualRate / 12;
  const factor = Math.pow(1 + monthlyRate, termMonths);
  return principal * (monthlyRate * factor) / (factor - 1);
}
function sum(values) {
  return values.reduce((acc, val) => acc + val, 0);
}
function average(values) {
  if (values.length === 0) return 0;
  return sum(values) / values.length;
}
function clamp(value, min, max) {
  return Math.min(Math.max(value, min), max);
}
function inRange(value, min, max) {
  return value >= min && value <= max;
}
function lerp(start, end, t) {
  return start + (end - start) * t;
}

// src/currency/index.ts
var currencyLocales = {
  USD: "en-US",
  EUR: "de-DE",
  GBP: "en-GB",
  CAD: "en-CA",
  AUD: "en-AU"
};
function formatCurrency(value, options = {}) {
  const {
    currency = "USD",
    locale = currencyLocales[currency],
    minimumFractionDigits = 0,
    maximumFractionDigits = 0,
    compact = false
  } = options;
  const formatter = new Intl.NumberFormat(locale, {
    style: "currency",
    currency,
    minimumFractionDigits,
    maximumFractionDigits,
    notation: compact ? "compact" : "standard"
  });
  return formatter.format(value);
}
function formatCurrencyWithCents(value, currency = "USD") {
  return formatCurrency(value, {
    currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });
}
function formatCurrencyCompact(value, currency = "USD") {
  return formatCurrency(value, {
    currency,
    compact: true,
    minimumFractionDigits: 1,
    maximumFractionDigits: 1
  });
}
function parseCurrency(value) {
  let cleaned = value.replace(/[^0-9.,\-KMB]/gi, "").trim();
  const multipliers = {
    K: 1e3,
    M: 1e6,
    B: 1e9
  };
  let multiplier = 1;
  const lastChar = cleaned.slice(-1).toUpperCase();
  if (multipliers[lastChar]) {
    multiplier = multipliers[lastChar];
    cleaned = cleaned.slice(0, -1);
  }
  const parsed = parseFloat(cleaned.replace(/,/g, ""));
  if (isNaN(parsed)) return 0;
  return parsed * multiplier;
}
function formatPercent(value, options = {}) {
  const { decimals = 2, locale = "en-US" } = options;
  const formatter = new Intl.NumberFormat(locale, {
    style: "percent",
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals
  });
  return formatter.format(value);
}
function formatPercentFromDecimal(decimal, decimals = 2) {
  return formatPercent(decimal, { decimals });
}
function formatNumber(value, options = {}) {
  const { decimals = 0, locale = "en-US" } = options;
  return new Intl.NumberFormat(locale, {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals
  }).format(value);
}

// src/date/index.ts
function monthsBetween(start, end) {
  const years = end.getFullYear() - start.getFullYear();
  const months = end.getMonth() - start.getMonth();
  return years * 12 + months;
}
function addMonths(date, months) {
  const result = new Date(date);
  result.setMonth(result.getMonth() + months);
  return result;
}
function addYears(date, years) {
  const result = new Date(date);
  result.setFullYear(result.getFullYear() + years);
  return result;
}
function startOfMonth(date) {
  return new Date(date.getFullYear(), date.getMonth(), 1);
}
function endOfMonth(date) {
  return new Date(date.getFullYear(), date.getMonth() + 1, 0);
}
function startOfYear(date) {
  return new Date(date.getFullYear(), 0, 1);
}
function endOfYear(date) {
  return new Date(date.getFullYear(), 11, 31);
}
function formatDateISO(date) {
  return date.toISOString().split("T")[0];
}
function formatMonthYear(date, locale = "en-US") {
  return date.toLocaleDateString(locale, { month: "short", year: "numeric" });
}
function formatDateLong(date, locale = "en-US") {
  return date.toLocaleDateString(locale, {
    month: "long",
    day: "numeric",
    year: "numeric"
  });
}
function generateMonthlyPeriods(startDate, months) {
  const periods = [];
  for (let i = 0; i < months; i++) {
    const date = addMonths(startDate, i);
    periods.push({
      month: i + 1,
      year: Math.floor(i / 12) + 1,
      date
    });
  }
  return periods;
}
function generateAnnualPeriods(startDate, years) {
  const periods = [];
  for (let i = 0; i < years; i++) {
    const start = addYears(startDate, i);
    const end = addYears(startDate, i + 1);
    end.setDate(end.getDate() - 1);
    periods.push({
      year: i + 1,
      startDate: start,
      endDate: end
    });
  }
  return periods;
}
function yearsToMonths(years) {
  return Math.round(years * 12);
}
function monthsToYears(months) {
  return months / 12;
}
export {
  addMonths,
  addYears,
  average,
  calculateMonthlyPayment,
  clamp,
  compoundGrowth,
  endOfMonth,
  endOfYear,
  formatCurrency,
  formatCurrencyCompact,
  formatCurrencyWithCents,
  formatDateISO,
  formatDateLong,
  formatMonthYear,
  formatNumber,
  formatPercent,
  formatPercentFromDecimal,
  fromPercent,
  futureValue,
  generateAnnualPeriods,
  generateMonthlyPeriods,
  inRange,
  lerp,
  monthsBetween,
  monthsToYears,
  parseCurrency,
  presentValue,
  round,
  roundCents,
  startOfMonth,
  startOfYear,
  sum,
  toPercent,
  yearsToMonths
};
//# sourceMappingURL=index.js.map